import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { Textarea } from 'src/@/components/ui/textarea';
import { CANAL_DETALHE_DESIGN as D } from './CanaisDetails.design';

// Peças compartilhadas do formulário de canal. Moram fora do CanaisConfig porque
// o ChannelDirect também as usa — importá-las de lá criaria um ciclo.
// ----------------------------------------------------------------------

// Rótulo de seção fora do cartão. Separa o assunto ("Identificação") dos
// campos, para o cartão não precisar carregar título e conteúdo ao mesmo tempo.
export function SectionHeader({ title, help }) {
    return (
        <div className={D.sectionHeader}>
            <h2 className={D.sectionHeaderTitle}>{title}</h2>
            {help && <p className={D.sectionHeaderSub}>{help}</p>}
        </div>
    );
}

// "Rótulo + explicação + interruptor": a unidade de configuração booleana
// repetida em Atendimentos e IA. `last` remove a divisória da última linha.
export function ToggleRow({ id, label, help, checked, onCheckedChange, beta, last, disabled }) {
    return (
        <div className={`${D.toggleRow} ${last ? '' : D.toggleRowDivided}`}>
            <div className={D.toggleRowMain}>
                <div className={D.toggleRowLabelWrap}>
                    <Label htmlFor={id} className={`${D.toggleRowLabel} cursor-pointer`}>
                        {label}
                    </Label>
                    {beta && <span className={D.betaBadge}>BETA</span>}
                </div>
                {help && <p className={D.toggleRowHelp}>{help}</p>}
            </div>
            <Switch
                id={id}
                checked={Boolean(checked)}
                disabled={disabled}
                onCheckedChange={onCheckedChange}
                className={D.switch}
            />
        </div>
    );
}

// Cartão de escolha única. Substitui o radio nu onde a opção precisa de uma
// linha de explicação para ser escolhida com consciência.
export function ChoiceCard({ label, help, selected, onSelect }) {
    return (
        <button
            type="button"
            onClick={onSelect}
            aria-pressed={selected}
            className={`${D.choiceCard} ${selected ? D.choiceCardOn : D.choiceCardOff}`}
        >
            <span className={`${D.choiceTitle} ${selected ? D.choiceTitleOn : D.choiceTitleOff} block`}>{label}</span>
            {help && <span className={`${D.choiceHelp} block`}>{help}</span>}
        </button>
    );
}

// O negrito com *asteriscos* é a sintaxe do WhatsApp/Telegram: a prévia mostra
// o resultado, não o texto cru — é justamente o que o usuário não consegue ver
// no campo de edição.
function formatarPreview(texto) {
    return String(texto ?? '')
        .split(/(\*[^*\n]+\*)/g)
        .map((parte, i) =>
            parte.startsWith('*') && parte.endsWith('*') && parte.length > 2 ? (
                <strong key={i} className="font-bold">
                    {parte.slice(1, -1)}
                </strong>
            ) : (
                <span key={i}>{parte}</span>
            )
        );
}

// Editor de mensagem: campo + atalhos de variável + prévia lado a lado.
// Mantém a própria ref para inserir a variável na posição do cursor — anexar no
// fim obrigaria o usuário a recortar e colar no lugar certo.
export function MessageEditor({ label, value, onChange, rows = 8, variables = [], placeholder, previewFoot, help }) {
    const { t } = useTranslation('settings');
    const areaRef = useRef(null);
    const texto = value ?? '';

    const inserirVariavel = (token) => {
        const el = areaRef.current;
        if (!el || typeof el.selectionStart !== 'number') {
            onChange(`${texto}${token}`);
            return;
        }
        const inicio = el.selectionStart;
        const fim = el.selectionEnd;
        onChange(`${texto.slice(0, inicio)}${token}${texto.slice(fim)}`);
        const cursor = inicio + token.length;
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(cursor, cursor);
        });
    };

    return (
        <div className={D.editorGrid}>
            <div className="min-w-0">
                {label && <label className={D.label}>{label}</label>}
                <Textarea
                    ref={areaRef}
                    rows={rows}
                    value={texto}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                    className={D.textarea}
                />
                {help && <p className={D.hint}>{help}</p>}
                {/* Só entram aqui variáveis que o servidor realmente substitui —
                    um atalho que não vira nada no envio é pior que atalho nenhum. */}
                <div className={D.varsRow}>
                    {variables.map((token) => (
                        <button key={token} type="button" className={D.varChip} onClick={() => inserirVariavel(token)}>
                            {token}
                        </button>
                    ))}
                    <span className={D.varsHint}>{t('channels.messages.formattingHint', '*negrito* · _itálico_')}</span>
                </div>
            </div>
            <div className="min-w-0">
                <div className={D.previewLabel}>{t('channels.messages.previewLabel', 'Prévia')}</div>
                <div className={D.previewShell}>
                    <div className={D.previewBubble}>
                        {texto.trim() ? (
                            formatarPreview(texto)
                        ) : (
                            <span className={D.previewEmpty}>
                                {t('channels.messages.previewEmpty', 'A mensagem aparece aqui conforme você escreve.')}
                            </span>
                        )}
                    </div>
                    {previewFoot && <div className={D.previewFoot}>{previewFoot}</div>}
                </div>
            </div>
        </div>
    );
}
